import { useEffect, useMemo, useState } from "react";
import { CaPageHeader, usePath } from "@/company-admin/shared";
import { Button } from "@/admin/ui";
import { caApi, formatApiError } from "@/company-admin/api";

export default function Roles() {
    const { data: roles, loading, refresh } = usePath("/company-admin/roles");
    const { data: perms } = usePath("/company-admin/permissions");
    const [active, setActive] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => { if (!active && roles?.length) setActive(roles[0]); }, [roles, active]);
    useEffect(() => { if (active) setSelected(new Set(active.permissions_effective || [])); }, [active]);

    const dirty = useMemo(() => {
        if (!active) return false;
        const orig = new Set(active.permissions_effective || []);
        if (orig.size !== selected.size) return true;
        for (const k of selected) if (!orig.has(k)) return true;
        return false;
    }, [selected, active]);

    const locked = active && ["platform_admin", "company_admin"].includes(active.key);

    function toggle(k) {
        if (locked) return;
        const next = new Set(selected);
        next.has(k) ? next.delete(k) : next.add(k);
        setSelected(next);
    }

    async function save() {
        if (!active || locked) return;
        setBusy(true); setErr("");
        try {
            await caApi.patch(`/company-admin/roles/${active.id}`, { permissions: Array.from(selected) });
            await refresh();
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div data-testid="ca-roles-page">
            <CaPageHeader
                title="Roles"
                subtitle="Company-scoped permission overrides. Global role permissions are managed by the platform admin."
            />

            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
                <aside className="glass rounded-3xl p-3">
                    <ul className="space-y-1">
                        {loading && <li className="p-4 text-sm text-neutral-500">Loading…</li>}
                        {(roles || []).map((r) => (
                            <li key={r.id}>
                                <button
                                    onClick={() => setActive(r)}
                                    data-testid={`ca-role-${r.key}`}
                                    className={`w-full text-left rounded-2xl px-4 py-3 transition-all ${active?.id === r.id ? "bg-black text-white" : "hover:bg-black/[0.04]"}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="text-[13.5px] font-semibold">{r.name}</div>
                                        {r.overridden && (
                                            <span className={`text-[9px] font-semibold tracking-[0.18em] uppercase rounded-full px-1.5 py-0.5 ${active?.id === r.id ? "bg-white/20" : "bg-violet-50 text-violet-700"}`}>
                                                override
                                            </span>
                                        )}
                                    </div>
                                    <div className={`text-[11px] mt-0.5 ${active?.id === r.id ? "text-white/70" : "text-neutral-500"}`}>
                                        {r.permissions_effective?.length || 0} permissions
                                    </div>
                                </button>
                            </li>
                        ))}
                    </ul>
                </aside>

                <section className="glass rounded-3xl p-6 sm:p-8">
                    {active ? (
                        <>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-violet-600">Role</div>
                                    <div className="mt-1 font-display text-2xl sm:text-3xl font-medium text-black">{active.name}</div>
                                    <div className="mt-1 text-[13px] text-neutral-500">
                                        {selected.size} of {(perms || []).length} permissions {active.overridden ? "· company override" : "· inherits platform default"}
                                    </div>
                                </div>
                                <Button onClick={save} disabled={!dirty || busy || locked} data-testid="ca-role-save">
                                    {busy ? "Saving…" : locked ? "Locked" : "Save changes"}
                                </Button>
                            </div>
                            {err && <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50/70 text-rose-700 text-[13px] px-3.5 py-2.5">{err}</div>}
                            {locked && (
                                <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50/60 text-violet-700 text-[13px] px-3.5 py-2.5">
                                    Admin roles can only be configured by the platform admin.
                                </div>
                            )}

                            <div className="mt-6 grid sm:grid-cols-2 gap-2.5">
                                {(perms || []).map((p) => {
                                    const on = selected.has(p.key);
                                    return (
                                        <button
                                            key={p.key}
                                            onClick={() => toggle(p.key)}
                                            disabled={locked}
                                            data-testid={`ca-perm-${p.key}`}
                                            className={`flex items-center justify-between rounded-2xl border px-4 py-3 text-left transition-all ${
                                                on ? "border-violet-200 bg-violet-50/60" : "border-black/5 bg-white hover:bg-neutral-50"
                                            } ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
                                        >
                                            <span className="text-[13.5px] text-black">{p.label}</span>
                                            <span className={`inline-flex h-5 w-9 rounded-full transition-all ${on ? "bg-violet-500" : "bg-neutral-200"}`}>
                                                <span className={`h-4 w-4 rounded-full bg-white shadow transition-all mt-0.5 ${on ? "translate-x-4" : "translate-x-0.5"}`} />
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    ) : (
                        <div className="text-sm text-neutral-500">Select a role.</div>
                    )}
                </section>
            </div>
        </div>
    );
}
